import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  InputAdornment,
  useTheme,
  alpha,
  Stack,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Apps as AppsIcon,
} from '@mui/icons-material';
import { agentCategories } from '../config/agentCategories';
import { agentConfigs } from '../config/agentConfig';

const AgentsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Get only agents that are currently enabled in sidebar categories
  const allAgents = useMemo(() => {
    const enabledAgentIds = new Set();
    agentCategories.forEach(category => {
      category.agents.forEach(agent => {
        enabledAgentIds.add(agent.id);
      });
    });
    return agentConfigs.filter(agent => 
      agent.isImplemented && enabledAgentIds.has(agent.id)
    );
  }, []);

  // Filter agents based on search and category
  const filteredAgents = useMemo(() => {
    let filtered = allAgents;

    // Filter by category
    if (selectedCategory) {
      const category = agentCategories.find(cat => cat.id === selectedCategory);
      if (category) {
        const categoryAgentIds = category.agents.map(agent => agent.id);
        filtered = filtered.filter(agent => categoryAgentIds.includes(agent.id));
      }
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(agent => 
        agent.title.toLowerCase().includes(query) ||
        agent.description.toLowerCase().includes(query) ||
        agent.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [allAgents, selectedCategory, searchQuery]);

  const handleAgentClick = (agent: typeof agentConfigs[0]) => {
    navigate(agent.route);
  };

  const handleCategoryFilter = (categoryId: string | null) => {
    setSelectedCategory(categoryId);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            mb: 1,
            color: theme.palette.text.primary
          }}
        >
          Creative Agents
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: theme.palette.text.secondary,
            mb: 3
          }}
        >
          Choose from {allAgents.length} specialized AI agents to supercharge your workflow
        </Typography>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search agents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
              },
              '&.Mui-focused': {
                backgroundColor: theme.palette.background.paper,
              },
            },
          }}
        />

        {/* Category Filters */}
        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label="All"
            variant={selectedCategory === null ? 'filled' : 'outlined'}
            onClick={() => handleCategoryFilter(null)}
            sx={{
              fontWeight: selectedCategory === null ? 600 : 400,
              backgroundColor: selectedCategory === null 
                ? theme.palette.primary.main 
                : 'transparent',
              color: selectedCategory === null 
                ? theme.palette.primary.contrastText 
                : theme.palette.text.primary,
              '&:hover': {
                backgroundColor: selectedCategory === null 
                  ? theme.palette.primary.dark
                  : alpha(theme.palette.primary.main, 0.08),
              },
            }}
          />
          {agentCategories.map((category) => (
            <Badge
              key={category.id}
              badgeContent={category.agents.length}
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  height: 16,
                  minWidth: 16,
                },
              }}
            >
              <Chip
                icon={<category.icon fontSize="small" />}
                label={category.name}
                variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                onClick={() => handleCategoryFilter(
                  selectedCategory === category.id ? null : category.id
                )}
                sx={{
                  fontWeight: selectedCategory === category.id ? 600 : 400,
                  backgroundColor: selectedCategory === category.id 
                    ? theme.palette.primary.main 
                    : 'transparent',
                  color: selectedCategory === category.id 
                    ? theme.palette.primary.contrastText 
                    : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: selectedCategory === category.id 
                      ? theme.palette.primary.dark
                      : alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              />
            </Badge>
          ))}
        </Stack>
      </Box>

      {/* Results Count */}
      <Typography 
        variant="body2" 
        sx={{ 
          color: theme.palette.text.secondary,
          mb: 2,
          fontWeight: 500
        }}
      >
        {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''} found
      </Typography>

      {/* Agents List */}
      <Paper 
        elevation={0}
        sx={{ 
          backgroundColor: alpha(theme.palette.background.paper, 0.6),
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <List sx={{ p: 0 }}>
          {filteredAgents.map((agent, index) => {
            const AgentIcon = agent.icon;
            return (
              <ListItem 
                key={agent.id} 
                disablePadding
                sx={{
                  borderBottom: index < filteredAgents.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                }}
              >
                <ListItemButton
                  onClick={() => handleAgentClick(agent)}
                  sx={{
                    py: 2,
                    px: 3,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 56 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 1,
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      }}
                    >
                      <AgentIcon 
                        sx={{ 
                          fontSize: 24, 
                          color: theme.palette.primary.main 
                        }} 
                      />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '1rem',
                          mb: 0.5
                        }}
                      >
                        {agent.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.palette.text.secondary,
                            mb: 1,
                            lineHeight: 1.4
                          }}
                        >
                          {agent.description}
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          {agent.tags.slice(0, 3).map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: alpha(theme.palette.background.default, 0.5),
                                borderColor: alpha(theme.palette.text.secondary, 0.2),
                                color: theme.palette.text.secondary,
                                '& .MuiChip-label': {
                                  px: 1,
                                },
                              }}
                            />
                          ))}
                          {agent.tags.length > 3 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme.palette.text.secondary,
                                alignSelf: 'center',
                                ml: 0.5
                              }}
                            >
                              +{agent.tags.length - 3} more
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Paper>

      {/* Empty State */}
      {filteredAgents.length === 0 && (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            color: theme.palette.text.secondary
          }}
        >
          <AppsIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No agents found
          </Typography>
          <Typography variant="body2">
            Try adjusting your search or filter criteria
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default AgentsDashboard;